<template>
    <a-modal
        class="o-edit-resource"
        name="edit-resource"
        :resizable="true"
        :height="650"
        :minWidth="600"
        :minHeight="650"
        @onClose="onModalCloseHandler"
    >
            <h2>
                {{ $t('app.EditResource') }}
            </h2>
            <b-spinner v-if="isDataLoading"/>
            <div v-else class="modal-form-holder">
                <ValidationObserver
                    ref="editResource"
                    tag="form"
                    @submit.prevent="editResource"
                    class="modal-form"
                >
                    <div class="columns-holder">
                        <template v-if="resourceKind != resourceKindGoogledoc">
                            <span class="field-label">{{ $t('app.ResourceKind') }}</span>
                            <RadioGroup class="field resource-kind" v-model="resourceKind" @on-change="resourceChange">
                                <Radio :disabled="disableSafetyValueChange" :label="resourceKindString">
                                    <span>{{ $t('app.SimpleText') }}</span>
                                </Radio>
                                <Radio :disabled="disableSafetyValueChange" :label="resourceKindRichtext">
                                    <span>{{ $t('app.HtmlDocument') }}</span>
                                </Radio>
                            </RadioGroup>

                            <a-input
                                disabled
                                v-model="resourceKey"
                                :validation="true"
                                :validation-config="{
                                    name: 'resourceKey',
                                    rules: {
                                        required: true,
                                    },
                                }"
                                class="app-row-form field"
                                :label="$t('app.Key')"
                                :placeholder="$t('app.EnterResourceKey')"
                            >
                                <template v-if="!isNewResourceKey" v-slot:action>
                                    <Button :disabled="disableSafetyValueChange" @click="onOpenNewKey" class="ivu-btn-icon" type="primary" :title="$t('app.EditKey')">
                                        <i class="uil uil-edit"/>
                                    </Button>
                                </template>
                            </a-input>
                            <a-input
                                v-if="isNewResourceKey"
                                :disabled="disableSafetyValueChange"
                                v-model="newResourceKey"
                                :validation="true"
                                :validation-config="{
                                    name: 'newResourceKey',
                                    rules: {
                                        required: true,
                                    },
                                }"
                                class="app-row-form field"
                                :label="$t('app.NewEditKey')"
                                :placeholder="$t('app.EnterNewResourceKey')"
                            >
                                <template v-slot:action>
                                    <Button @click="onOpenNewKey" class="ivu-btn-icon" type="primary" :title="$t('app.HideNewKey')">
                                        <i class="uil uil-multiply"/>
                                    </Button>
                                </template>
                            </a-input>
                        </template>
                        <template v-else>
                            <span class="field-label">{{ $t('app.DocUrl') }}</span>
                            <div class="field">
                                <a :href="resourceUrl" target="_blank">
                                    {{ resourceName }} 
                                </a>
                            </div>
                        </template>

                        <span class="field-label">{{ $t('app.PrimaryLanguage') }}</span>
                        <Select :disabled="disableSafetyValueChange" class="field" v-model="langCode">
                            <Option 
                                v-for="lang in languages" 
                                :key="lang.code" 
                                :value="lang.code">
                                {{ lang.name }} ({{ lang.code }})
                            </Option>
                        </Select>
                        
                        <template v-if="resourceKind != resourceKindGoogledoc">
                            <span class="field-label">{{ $t('app.Value') }}</span>
                            <div class="switch-wrap">
                                <div class="switch-row">
                                    <i-switch @on-change="onChangeSafetyValueChange" v-model="safetyValueChange" />
                                    <span class="switch-label">{{ $t('app.ChangingMeaningOnlyPrimaryLanguage') }}</span>
                                </div>
                            </div>
                            <ValidationProvider
                                v-slot="{ errors }"
                                name="resourceValue"
                                :rules="{
                                    required: true,
                                }"
                                mode="eager"
                                class="modal-form-textarea"
                            >
                                <div class="field">
                                    <template v-if="resourceKind == resourceKindString">
                                        <b-form-textarea
                                            v-model="resourceValue"
                                            rows="6"
                                            max-rows="6"
                                        />
                                    </template>
                                    <template v-else>
                                        <ckeditor @ready="onEditorLoadReady" class="editor-add-plagin-hidden"/>
                                        <b-spinner v-if="!isEditorAddPlagin" />
                                        <ckeditor
                                            v-if="isEditorAddPlagin"
                                            v-model="resourceValue"
                                            :config="editorConfig"
                                            class="popup-editor__value"
                                            @ready="onEditorReady"
                                        />
                                    </template>    
                                    <span class="app-error">{{ errors[0] }}</span>
                                </div>
                            </ValidationProvider>
                        </template>

                        <span class="field-label">{{ $t('app.Tags') }}</span>
                        <Select
                            :disabled="disableSafetyValueChange"
                            class="field"
                            v-model="selectedTags"
                            multiple
                            filterable
                            allow-create
                            @on-create="handleCreateTag"
                        >
                            <Option
                                v-for="(tag, index) in tags"
                                :key="index"
                                :value="tag"
                                >{{ tag }}</Option
                            >
                        </Select>

                        <span class="field-label">{{ $t('app.ResourceDescription') }}</span>
                        
                        <a-textarea
                            :disabled="disableSafetyValueChange"
                            v-model="resourceDescription"
                            rows="2"
                            max-rows="2"
                            :validation="true"
                            :validation-config="{
                                name: 'resourceDescription',
                                rules: {
                                    max: 1000,
                                },
                            }"
                        />
                    </div>

                    <div class="btns-holder">
                        <Button 
                            :disabled="uploadFileInProgress"
                            :loading="isSavingInProgress" 
                            type="primary" 
                            html-type="submit"
                        >
                            {{ $t('app.Save') }}
                        </Button>
                    </div>
                </ValidationObserver>
            </div>
    </a-modal>
</template>

<script>
import { Button, Select, Option, AutoComplete, RadioGroup, Radio, Switch } from 'view-design'
import AModal from '@atoms/a-modal'
import AAutoComplete from '@atoms/a-auto-complete'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import AInput from '@atoms/a-input'
import _ from 'lodash'
import { delay } from '@utils/helpers'
import { Role } from '@/constants/role.js'
import { ResourceKind } from '@/constants/resource-kind.js'
import { ChangingRoleForOwnerError, OwnerAlreadyExistsError } from '@utils/errors'
import ATextarea from '@atoms/a-textarea'
import CkImageUpload from '@libs/ck-image-upload/index'
import CkImageUploadCss from '@libs/ck-image-upload/css'
import { oembedReplace } from '@utils/oembed/'

export default {
    name: 'OEditResource',
    components: {
        AutoComplete,
        AAutoComplete,
        AModal,
        Button,
        ValidationObserver,
        ValidationProvider,
        AInput,
        Select, 
        Option,
        RadioGroup, 
        Radio,
        'i-switch': Switch,
        ATextarea,
    },
    props: {
        onEditResource: {
            type: Function,
            required: true,
        },
        onGetResourceByKey: {
            type: Function,
            required: true,
        },
        onAddGoogleDoc: {
            type: Function,
            required: true,
        },
        onUploadFile: {
            type: Function,
            required: true
        },
        languages: {
            type: Array,
            required: true,
        },
        tags: {
            type: Array,
            required: true,
        },
    },
    computed: {
    },
    watch: {
    },
    created() {
    },
    data() {
        return {
            safetyValueChange: false,
            disableSafetyValueChange: false,
            isNewResourceKey: false,
            isDataLoading: false,
            uploadFileInProgress: false,
            resourceKind: ResourceKind.string,
            resourceKey: '',
            newResourceKey: '',
            resourceName: '',
            resourceUrl: '',
            resourceDescription: '',
            isSavingInProgress: false,
            selectedTags: [],
            resourceValue: "",
            langCode: "",
            isEditorAddPlagin: false,
            editorConfig: {},
            defResource: {}
        }
    },
    computed: {
        resourceKindString() {
            return ResourceKind.string;
        },
        resourceKindRichtext() {
            return ResourceKind.richtext;
        },
        resourceKindGoogledoc() {
            return ResourceKind.googledoc;
        },
    },
    methods: {
        onModalCloseHandler() {
            this.$modal.hide('edit-resource');
        },
        async show(resource) {
            this.isDataLoading = true;
            this.$modal.show('edit-resource');
            
            const r = await this.onGetResourceByKey(resource.key);

            this.safetyValueChange = false;
            this.disableSafetyValueChange = false;

            this.isNewResourceKey = false;
            this.newResourceKey = '';
            this.resourceKind = resource.kind;
            this.resourceKey = resource.key;
            this.resourceUrl = resource.url ? resource.url : '';
            this.resourceDescription = resource.description ? resource.description : '';
            this.isSavingInProgress = false;
            this.selectedTags = resource.tags;
            this.resourceValue = r.value
            this.langCode = resource.primaryLanguage;
            this.resourceName = resource.name;

            this.defResource = JSON.parse(JSON.stringify({
                isNewResourceKey: this.isNewResourceKey,
                newResourceKey: this.newResourceKey,
                resourceKind: this.resourceKind,
                resourceKey: this.resourceKey,
                resourceUrl: this.resourceUrl,
                resourceDescription: this.resourceDescription,
                selectedTags: this.selectedTags,
                langCode: this.langCode,
                resourceName: this.resourceName,
            }));

            this.isDataLoading = false;
        },
        async handleCreateTag(tag) {
            this.selectedTags.push(tag)
        },
        async editResource() {
            const isValid = await this.$refs.editResource.validate()
            if (!isValid) return

            try {
                this.isSavingInProgress = true;
                
                if (this.resourceKind == this.resourceKindGoogledoc) {
                    await this.onAddGoogleDoc({
                        langCode: this.langCode,
                        url: this.resourceUrl,
                        description: this.resourceDescription,
                        tags: this.selectedTags,
                    })
                } else {
                    await this.onEditResource({
                        langCode: this.langCode,
                        key: this.resourceKey,
                        safetyValueChange: this.safetyValueChange && this.safetyValueChange ? this.safetyValueChange : undefined,
                        newKey: this.isNewResourceKey && this.newResourceKey ? this.newResourceKey : undefined,
                        value: this.resourceValue,
                        description: this.resourceDescription,
                        tags: this.selectedTags,
                        kind: this.resourceKind,
                    });
                }

                this.isSavingInProgress = false;
                this.$modal.hide('edit-resource');
            } catch (err) {
                this.isSavingInProgress = false;
                throw err;
            }
        },
        onEditorLoadReady() {
            if(window.CKEDITOR) {
                CkImageUpload(async file => {
                    let res = ''
                    if (this.onUploadFile) {
                        res = await this.onUploadFile(file)
                    }
                    return res
                });
                setTimeout(() => {
                    this.editorConfig = {
                        allowedContent: true,
                        language: this.$i18n.locale,
                        extraPlugins: 'embed,autoembed,image2,apiimage',
                        removePlugins: 'image',
                        removeButtons: 'Cut,Copy,Paste,PasteText,PasteFromWord,About,Maximize,HorizontalRule,SpecialChar,Subscript,Superscript,Scayt',
                        embed_provider: '//ckeditor.iframe.ly/api/oembed?url={url}&callback={callback}',
                        contentsCss: CkImageUploadCss,
                    };
                    this.isEditorAddPlagin = true;
                }, 0);
            }
        },
        onEditorReady(editor) {
            if (this.resourceValue && this.resourceValue !== '') {
                this.resourceValue = oembedReplace(this.resourceValue)
            }
            editor.on('mode', () => {
                this.uploadFileInProgress = editor.mode === 'source'
            });
        },
        resourceChange() {
            this.uploadFileInProgress = false
        },
        onOpenNewKey() {
            this.isNewResourceKey = !this.isNewResourceKey
        },
        onChangeSafetyValueChange(val) {
            if (val) {
                this.isNewResourceKey = this.defResource.isNewResourceKey;
                this.newResourceKey = this.defResource.newResourceKey;
                this.resourceKind = this.defResource.resourceKind;
                this.resourceKey = this.defResource.resourceKey;
                this.resourceUrl = this.defResource.resourceUrl;
                this.resourceDescription = this.defResource.resourceDescription;
                this.selectedTags = this.defResource.selectedTags;
                this.langCode = this.defResource.langCode;
                this.resourceName = this.defResource.resourceName;
            }
            this.disableSafetyValueChange = val;
        }
    },
}
</script>

<style lang="scss">
@import 'theme';
</style>
